import { darkTheme } from '../../lib/tokens';
import type { AnnouncementType } from './types';

// Spiegelt AnnouncementSheet aus curalearn-app/src/components/announcements/.
// Tokens müssen identisch zur App sein (siehe lib/tokens.ts). Wenn die App
// Layout-Änderungen macht, hier nachziehen.

const TYPE_META: Record<
  AnnouncementType,
  { label: string; icon: string; color: string; bg: string }
> = {
  NEWS: { label: 'News', icon: '📣', color: darkTheme.colors.info, bg: darkTheme.colors.infoBg },
  UPDATE: {
    label: 'Update',
    icon: '✨',
    color: darkTheme.colors.accent,
    bg: darkTheme.colors.accentSubtle,
  },
  PROMO: {
    label: 'Aktion',
    icon: '🎁',
    color: darkTheme.colors.warning,
    bg: darkTheme.colors.warningBg,
  },
  TECH: {
    label: 'Tech-Hinweis',
    icon: 'ℹ️',
    color: darkTheme.colors.textSecondary,
    bg: darkTheme.colors.bgElevated,
  },
  MAINTENANCE: {
    label: 'Wartung',
    icon: '🛠',
    color: darkTheme.colors.danger,
    bg: darkTheme.colors.dangerBg,
  },
};

interface MobilePreviewProps {
  title: string;
  body: string;
  type: AnnouncementType;
  imageUrl: string | null;
  ctaLabel: string | null;
  ctaUrl: string | null;
  dismissible: boolean;
}

export function MobilePreview(props: MobilePreviewProps): JSX.Element {
  const meta = TYPE_META[props.type];
  const hasCta = props.ctaUrl && props.ctaLabel;

  return (
    <div className="flex flex-col items-center">
      <div className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-text-tertiary">
        Vorschau
      </div>
      <div
        className="relative overflow-hidden rounded-[40px] border-8"
        style={{
          width: 280,
          height: 560,
          borderColor: '#1A1A2E',
          background: '#0F0F1E',
        }}
      >
        {/* Notch */}
        <div
          className="absolute left-1/2 top-2 h-5 w-20 -translate-x-1/2 rounded-full"
          style={{ background: '#1A1A2E' }}
        />

        {/* Dimmed app underneath */}
        <div className="absolute inset-0 bg-black/40" />

        {/* Modal card — vertically centered with overlay */}
        <div className="absolute inset-0 flex items-center justify-center px-4">
          <div
            className="relative w-full overflow-hidden rounded-[20px] p-5 shadow-2xl"
            style={{
              background: darkTheme.colors.bgSurface,
              maxHeight: '85%',
            }}
          >
            {props.dismissible && (
              <span
                aria-hidden
                className="absolute right-3 top-3 text-text-secondary"
                style={{ fontSize: 16 }}
              >
                ×
              </span>
            )}

            {props.imageUrl ? (
              <div
                className="mb-3 h-24 w-full rounded-md bg-cover bg-center"
                style={{
                  backgroundImage: `url(${props.imageUrl})`,
                  backgroundColor: darkTheme.colors.bgElevated,
                }}
              />
            ) : (
              <div className="mb-3 flex justify-center">
                <div
                  className="flex h-12 w-12 items-center justify-center rounded-full text-2xl"
                  style={{ background: meta.bg }}
                >
                  <span>{meta.icon}</span>
                </div>
              </div>
            )}

            <div
              className="mx-auto mb-3 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase"
              style={{ background: meta.bg, color: meta.color }}
            >
              {meta.label}
            </div>

            <h3
              className="mb-2 text-center text-base font-bold"
              style={{ color: darkTheme.colors.textPrimary, fontFamily: 'Inter' }}
            >
              {props.title || 'Titel — bitte eingeben'}
            </h3>
            <p
              className="mb-4 text-center text-xs leading-relaxed"
              style={{
                color: darkTheme.colors.textSecondary,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {props.body || 'Body-Text — bitte eingeben.'}
            </p>

            {hasCta ? (
              <div className="flex flex-col gap-2">
                <button
                  type="button"
                  className="rounded-md px-3 py-2 text-xs font-semibold text-white"
                  style={{ background: darkTheme.colors.accent }}
                >
                  {props.ctaLabel}
                </button>
                {props.dismissible && (
                  <button
                    type="button"
                    className="rounded-md px-3 py-2 text-xs"
                    style={{
                      background: darkTheme.colors.bgElevated,
                      color: darkTheme.colors.textSecondary,
                    }}
                  >
                    Später
                  </button>
                )}
              </div>
            ) : (
              <button
                type="button"
                className="w-full rounded-md px-3 py-2 text-xs font-semibold text-white"
                style={{ background: darkTheme.colors.accent }}
              >
                Verstanden
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
